import { useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  ComboBox as AriaComboBox,
  type ComboBoxProps as AriaComboBoxProps,
  Button,
  ListBox,
  Popover,
  type ValidationResult,
} from 'react-aria-components';
import cx from 'clsx';
import { IconChevronBottomOutlined } from '@repo/monochrome-icons';
import {
  Description,
  Error,
  Input,
  Label,
  Option,
  OptionDivider,
  OptionGroup,
} from '../form-elements';
import styles from './combo-box.module.css';

export interface ComboBoxProps<T extends object> extends Omit<AriaComboBoxProps<T>, 'children'> {
  label: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder: string;
  emptyMessage?: string;
  maxLength?: number;
  multiple?: boolean;
  isOptional?: boolean;
  isOptionalText?: string;
  children: ReactNode | ((item: T) => ReactNode);
  fullWidth?: boolean;
  withArrowButton?: boolean;
  placement?: 'bottom start' | 'bottom end';
  inputLabelIcon?: ReactNode;
  inputClassName?: string;
}

function ComboBoxComponent<T extends object>({
  label,
  className,
  description,
  errorMessage,
  placeholder,
  emptyMessage,
  maxLength,
  multiple,
  isOptional,
  isOptionalText,
  children,
  fullWidth = true,
  withArrowButton,
  inputLabelIcon,
  placement = 'bottom start',
  inputClassName,
  ...props
}: ComboBoxProps<T>): ReactElement {
  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;
  const inputContainerRef = useRef<HTMLSpanElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);

  // Measure trigger width for popover, the trigger’s size is no longer automatically tied to the input’s size
  useLayoutEffect(() => {
    if (!inputContainerRef.current) return;
    setPopoverWidth(inputContainerRef.current.offsetWidth);
  }, []);

  const isInvalid = Boolean(errorMessage);

  return (
    <AriaComboBox {...props} isInvalid={isInvalid}>
      {({ isOpen }) => (
        <>
          <div className={cx(className, styles.container)}>
            {Boolean(label) && (
              <Label>
                {label} {Boolean(isOptional) && isOptionalText}
              </Label>
            )}
            <span className={cx(styles['input-container'], inputClassName)} ref={inputContainerRef}>
              {inputLabelIcon ? (
                <span aria-hidden="true" className={styles['input-label-icon']}>
                  {inputLabelIcon}
                </span>
              ) : null}
              <Input maxLength={maxLength} multiple={multiple} placeholder={placeholder} />
              {withArrowButton ? (
                <Button>
                  <IconChevronBottomOutlined
                    className={cx(styles['chevron-icon'], isOpen && styles['chevron-icon-open'])}
                  />
                </Button>
              ) : null}
            </span>
            <Error>{errorMessage}</Error>
            {Boolean(description) && <Description>{description}</Description>}
          </div>
          <Popover
            UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
            className={cx('body-2', styles.popover, fullWidth && styles['full-width'])}
            data-combo-box-popover
            offset={8}
            placement={placement}
            style={popoverWidth ? { width: popoverWidth } : undefined}
            triggerRef={inputContainerRef}
          >
            <ListBox
              className={styles['option-list']}
              renderEmptyState={() =>
                emptyMessage ? <div className={styles['no-results']}>{emptyMessage}</div> : null
              }
            >
              {children}
            </ListBox>
          </Popover>
        </>
      )}
    </AriaComboBox>
  );
}

export const ComboBox = Object.assign(ComboBoxComponent, {
  Option,
  Divider: OptionDivider,
  Group: OptionGroup,
});
